// Copyright (c) 2026 Otyka LLC. All rights reserved.

import React, { useState, useEffect, useRef } from 'react';
import { getIdToken } from './auth';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export default function ReportGenerator({ tenants }) {
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [months, setMonths] = useState(3);
  const [title, setTitle] = useState('');
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const reportRef = useRef(null);

  function toggleTenant(tenantId) {
    setSelectedTenants((prev) =>
      prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId]
    );
  }

  function selectAll() {
    const activeTenants = tenants.filter((t) => t.status === 'active').map((t) => t.tenantId);
    setSelectedTenants(activeTenants);
  }

  function selectNone() {
    setSelectedTenants([]);
  }

  async function generateReport() {
    if (selectedTenants.length === 0) {
      setError('Select at least one club to include in the report.');
      return;
    }
    setGenerating(true);
    setError(null);
    setReport(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${API_BASE}/admin/reports/industry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          tenantIds: selectedTenants,
          months,
          title: title.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Report generation failed');
      setReport(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  async function exportPDF() {
    const html2pdf = (await import('html2pdf.js')).default;
    const element = reportRef.current;
    if (!element) return;

    const opt = {
      margin: [0.75, 0.75, 0.75, 0.75],
      filename: `${report.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
    };

    html2pdf().set(opt).from(element).save();
  }

  const activeTenants = tenants.filter((t) => t.status === 'active');

  return (
    <div className="report-gen">
      <div className="report-gen__header">
        <h2 className="report-gen__title">Industry Report Generator</h2>
        <p className="report-gen__subtitle">
          Generate a cross-club analyst report summarizing trends and areas of concern across selected organizations.
          This is a Steward.golf premium offering.
        </p>
      </div>

      {/* Configuration */}
      <div className="report-gen__config">
        <div className="report-gen__config-section">
          <label className="report-gen__label">Report Title (optional)</label>
          <input
            type="text"
            className="report-gen__input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Q2 2026 Country Club Industry Report"
            maxLength={120}
          />
        </div>

        <div className="report-gen__config-section">
          <label className="report-gen__label">Time Period</label>
          <select className="report-gen__select" value={months} onChange={(e) => setMonths(Number(e.target.value))}>
            <option value={1}>Last 1 month</option>
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
          </select>
        </div>

        <div className="report-gen__config-section">
          <div className="report-gen__label-row">
            <label className="report-gen__label">Clubs to Include ({selectedTenants.length}/{activeTenants.length})</label>
            <div className="report-gen__select-btns">
              <button type="button" className="report-gen__link-btn" onClick={selectAll}>Select all</button>
              <button type="button" className="report-gen__link-btn" onClick={selectNone}>Clear</button>
            </div>
          </div>
          <div className="report-gen__tenant-grid">
            {activeTenants.map((t) => (
              <label key={t.tenantId} className={`report-gen__tenant-option${selectedTenants.includes(t.tenantId) ? ' report-gen__tenant-option--selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedTenants.includes(t.tenantId)}
                  onChange={() => toggleTenant(t.tenantId)}
                />
                <span>{t.name}</span>
              </label>
            ))}
          </div>
        </div>

        {error && <div className="report-gen__error">{error}</div>}

        <button
          className="admin-btn admin-btn--primary"
          onClick={generateReport}
          disabled={generating || selectedTenants.length === 0}
          style={{ marginTop: 16 }}
        >
          {generating ? 'Generating report…' : `Generate Report (${selectedTenants.length} club${selectedTenants.length !== 1 ? 's' : ''})`}
        </button>
      </div>

      {/* Report output */}
      {report && (
        <div className="report-gen__output">
          <div className="report-gen__output-toolbar">
            <h3>Report Ready</h3>
            <button className="admin-btn admin-btn--primary" onClick={exportPDF}>
              ↓ Export PDF
            </button>
          </div>

          {/* Rendered report (used for PDF generation) */}
          <div ref={reportRef} className="report-gen__document">
            <div className="report-doc">
              <div className="report-doc__cover">
                <p className="report-doc__brand">STEWARD.GOLF</p>
                <h1 className="report-doc__title">{report.title}</h1>
                <p className="report-doc__meta">
                  {report.clubCount} club{report.clubCount > 1 ? 's' : ''} · {report.totalSubmissions} submissions analyzed · {report.periodMonths} month window
                </p>
                <p className="report-doc__date">Generated {new Date(report.generatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <p className="report-doc__clubs">Clubs included: {report.clubsIncluded.join(', ')}</p>
              </div>

              <div className="report-doc__kpis">
                <div className="report-doc__kpi">
                  <span className="report-doc__kpi-value">{report.totalSubmissions}</span>
                  <span className="report-doc__kpi-label">Total Submissions</span>
                </div>
                <div className="report-doc__kpi">
                  <span className="report-doc__kpi-value">{report.analytics.satisfactionScore !== null ? `${report.analytics.satisfactionScore > 0 ? '+' : ''}${report.analytics.satisfactionScore}%` : 'N/A'}</span>
                  <span className="report-doc__kpi-label">Satisfaction Score</span>
                </div>
                <div className="report-doc__kpi">
                  <span className="report-doc__kpi-value">{report.clubCount}</span>
                  <span className="report-doc__kpi-label">Clubs Analyzed</span>
                </div>
                <div className="report-doc__kpi">
                  <span className="report-doc__kpi-value">{report.analytics.topInvestmentAreas.length}</span>
                  <span className="report-doc__kpi-label">Investment Areas</span>
                </div>
              </div>

              {report.narrative.sections.map((section, i) => (
                <div key={i} className="report-doc__section">
                  <h2 className="report-doc__section-title">{section.title}</h2>
                  {section.content.split('\n\n').map((para, pi) => (
                    <p key={pi} className="report-doc__para">{para}</p>
                  ))}
                </div>
              ))}

              <div className="report-doc__footer">
                <p>© 2026 Otyka LLC — Steward.golf Industry Intelligence</p>
                <p>This report is confidential and intended for authorized recipients only.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
